/**
 * Print Page (v1 — Classic)
 * Generic Jinja-based print preview page for any doctype.
 * Uses iframe to render server-generated HTML print formats.
 * For React-based print templates, see PrintStudioPage.tsx (v2).
 */

import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Printer, Download, Loader2, ArrowLeft } from "lucide-react";
import { getPrintHtml, downloadPrintPdf, getPrintConfig } from "@/lib/print/printService";
import { logger } from "@/lib/utils/logger";
import { cn } from "@/lib/utils";

type FormatEntry = {
  name: string;
  label: string;
};

export function PrintPage() {
  const { doctype = "", name = "" } = useParams<{ doctype: string; name: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get URL params
  const format = searchParams.get("format") ?? "Standard";
  const no_letterhead = (searchParams.get("no_letterhead") ?? "0") as "0" | "1";
  const letterhead = searchParams.get("letterhead") ?? "";
  const language = searchParams.get("language") ?? searchParams.get("lang") ?? "";
  const autoprint = searchParams.get("autoprint") === "1";

  // State
  const [formats, setFormats] = useState<FormatEntry[]>([]);
  const [_defaultFormat, setDefaultFormat] = useState<string>("Standard");
  const [languages, setLanguages] = useState<Array<{ code: string; label: string }>>([]);
  const [defaultLanguage, setDefaultLanguage] = useState<string>("en");
  const [html, setHtml] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [company, setCompany] = useState<string | null>(null);
  const [companyLoaded, setCompanyLoaded] = useState(false);

  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Fetch the document's company field
  useEffect(() => {
    if (!doctype || !name) return;
    let stale = false;
    setCompanyLoaded(false);
    setConfigLoaded(false);

    import("@/lib/api/tenantServerClient").then(({ tenantServer }) => {
      tenantServer
        .call<{ company?: string }>("frappe.client.get_value", {
          doctype,
          filters: { name },
          fieldname: "company",
        })
        .then((res) => {
          if (!stale) setCompany(res?.company || null);
        })
        .catch(() => {
          if (!stale) setCompany(null);
        })
        .finally(() => {
          if (!stale) setCompanyLoaded(true);
        });
    });

    return () => {
      stale = true;
    };
  }, [doctype, name]);

  // Load print formats after company is resolved
  useEffect(() => {
    if (!doctype || !companyLoaded) return;
    let stale = false;

    getPrintConfig(doctype, company || undefined)
      .then((config) => {
        if (stale) return;
        const fetchedFormats = Array.isArray(config.formats) ? config.formats : [];
        // Filter to Jinja-only formats (exclude React renderer templates)
        const jinjaFormats = fetchedFormats
          .filter((f: any) => f.renderer !== "react")
          .map((f: any) => ({ name: f.name, label: f.label || f.name }));
        setFormats(jinjaFormats);
        const defaultFmt =
          typeof config.default_format === "string" ? config.default_format : "Standard";
        setDefaultFormat(defaultFmt);

        const fetchedLanguages = Array.isArray(config.languages) ? config.languages : [];
        setLanguages(fetchedLanguages);
        setDefaultLanguage(
          typeof config.default_language === "string" ? config.default_language : "en"
        );

        // Smart default selection
        const formatNames = jinjaFormats.map((f: FormatEntry) => f.name);
        const hasExplicitDefault = defaultFmt !== "Standard" && formatNames.includes(defaultFmt);
        const companyFormat = jinjaFormats.find((f: FormatEntry) => f.name !== "Standard");
        const bestDefault = hasExplicitDefault
          ? defaultFmt
          : companyFormat
            ? companyFormat.name
            : "Standard";

        const currentFormat = searchParams.get("format");
        if (!currentFormat || !formatNames.includes(currentFormat)) {
          const nextParams = new URLSearchParams(searchParams);
          nextParams.set("format", bestDefault);
          setSearchParams(nextParams, { replace: true });
        }

        setConfigLoaded(true);
      })
      .catch((err) => {
        if (!stale) {
          logger.error("Failed to load print config:", err);
          setFormats([]);
          setConfigLoaded(true);
        }
      });

    return () => {
      stale = true;
    };
  }, [doctype, company, companyLoaded]);

  // Load Jinja HTML preview
  useEffect(() => {
    if (!doctype || !name || !configLoaded) return;
    let stale = false;
    setLoading(true);
    setError(null);

    getPrintHtml(doctype, name, {
      format,
      letterhead: letterhead || undefined,
      no_letterhead: no_letterhead === "1" ? 1 : 0,
      language: language || undefined,
      company: company || undefined,
    })
      .then((htmlContent) => {
        if (!stale) setHtml(htmlContent);
      })
      .catch((err) => {
        if (!stale) {
          logger.error("Failed to load print preview:", err);
          setError(err?.message ?? "Failed to load print preview");
        }
      })
      .finally(() => {
        if (!stale) setLoading(false);
      });

    return () => {
      stale = true;
    };
  }, [doctype, name, format, letterhead, no_letterhead, language, company, configLoaded]);

  const handleFormatChange = (newFormat: string) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("format", newFormat);
    setSearchParams(nextParams, { replace: true });
  };

  const handleLanguageChange = (newLanguage: string) => {
    const nextParams = new URLSearchParams(searchParams);
    if (newLanguage === "__system__" || !newLanguage || newLanguage === defaultLanguage) {
      nextParams.delete("language");
      nextParams.delete("lang");
    } else {
      nextParams.set("language", newLanguage);
      nextParams.delete("lang");
    }
    setSearchParams(nextParams, { replace: true });
  };

  const handleDownload = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const blob = await downloadPrintPdf(doctype, name, {
        format,
        letterhead: letterhead || undefined,
        no_letterhead: no_letterhead === "1" ? 1 : 0,
        language: language || undefined,
        company: company || undefined,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${doctype}-${name}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      logger.error("Failed to download PDF:", err);
      setError(err instanceof Error ? err.message : "Failed to download PDF");
    } finally {
      setLoading(false);
    }
  }, [doctype, name, format, letterhead, no_letterhead, language, company]);

  const handlePrint = useCallback(() => {
    const win = iframeRef.current?.contentWindow;
    if (!win) {
      logger.error("Print iframe not available");
      return;
    }
    win.focus();
    win.print();
  }, []);

  // Auto-trigger print dialog when opened from pack station flow
  const autoPrintFired = useRef(false);
  const handleIframeLoad = useCallback(() => {
    if (autoprint && html && !autoPrintFired.current) {
      autoPrintFired.current = true;
      // Small delay to ensure content is fully rendered
      setTimeout(() => {
        handlePrint();
      }, 300);
    }
  }, [autoprint, html, handlePrint]);

  if (!doctype || !name) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="text-sm text-muted-foreground">Invalid print parameters</div>
      </div>
    );
  }

  const formatOptions: FormatEntry[] =
    formats.length > 0 ? formats : [{ name: format, label: format }];

  return (
    <div className="flex h-full w-full flex-col gap-2 p-3">
      {/* Header */}
      <div className="flex items-center gap-4">
        {/* Left: back + name */}
        <div className="flex items-center gap-3 shrink-0">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="h-7 w-7 shrink-0"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
          </Button>
          <span className="text-xs text-muted-foreground">{name}</span>
        </div>

        {/* Center: format dropdown */}
        <div className="flex flex-1 items-center justify-center">
          <Select value={format} onValueChange={handleFormatChange}>
            <SelectTrigger className="h-8 w-[280px] rounded-full px-4 text-xs bg-card border border-border/40 shadow-sm">
              <SelectValue placeholder="Format" />
            </SelectTrigger>
            <SelectContent>
              {formatOptions.map((fmt) => (
                <SelectItem key={fmt.name} value={fmt.name}>
                  {fmt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Right: language + actions */}
        <div className="flex items-center gap-2 shrink-0">
          {languages.length > 0 && (
            <Select value={language || "__system__"} onValueChange={handleLanguageChange}>
              <SelectTrigger className="h-8 w-[180px] rounded-full px-4 text-xs bg-card border border-border/40 shadow-sm whitespace-nowrap">
                <SelectValue placeholder="Language" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__system__">System Default</SelectItem>
                {languages.map((lang) => (
                  <SelectItem key={lang.code} value={lang.code}>
                    {lang.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownload}
            disabled={loading}
            className="h-8 text-xs bg-card border-border/40 shadow-sm text-foreground"
          >
            {loading ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Download className="mr-1.5 h-3.5 w-3.5" />
            )}
            Download
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrint}
            disabled={loading || !html}
            className="h-8 text-xs bg-card border-border/40 shadow-sm text-foreground"
          >
            <Printer className="mr-1.5 h-3.5 w-3.5" />
            Print
          </Button>
        </div>
      </div>

      {/* Jinja iframe preview */}
      <div className="relative flex flex-1 overflow-hidden rounded-sm border">
        <div className="relative flex-1 overflow-hidden bg-white dark:bg-gray-950">
          {loading && (
            <div className="absolute left-4 top-4 z-10 flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading preview...
            </div>
          )}
          {error && (
            <div className="absolute left-4 top-4 z-10 rounded-xs bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <iframe
            ref={iframeRef}
            title="print-preview"
            className={cn("h-full w-full border-0", loading && "opacity-50")}
            sandbox="allow-same-origin allow-modals"
            srcDoc={html || "<!doctype html><html><head></head><body></body></html>"}
            onLoad={handleIframeLoad}
          />
        </div>
      </div>
    </div>
  );
}
